/**
 * DepositSection - Deposit flow content
 *
 * Shows:
 * - Bonus percentage (pulsing, color-shifting)
 * - Safe vault with interactive keypad
 * - Calculator overlay for entering amount
 * - Bonus animation sequence
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { cn } from '@/lib/utils';
import { Calculator } from '@/components/shared/calculator/Calculator';
import { ButtonType } from '@/components/shared/calculator/CalculatorButton';
import { useSound } from '@/hooks';

// ============================================
// TYPES
// ============================================

export interface DepositSectionProps {
  /** Current bonus percentage (0 = no bonus) */
  bonusPercentage?: number;
  /** Loading state */
  isLoading?: boolean;
  /** Callback when deposit amount is confirmed */
  onDepositConfirm?: (amount: number, withBonus: number) => void;
}

// ============================================
// SEGMENT MAP FOR SEVEN-SEGMENT DISPLAY
// ============================================

const SEGMENT_MAP: Record<string, string[]> = {
  '0': ['a', 'b', 'c', 'd', 'e', 'f'],
  '1': ['b', 'c'],
  '2': ['a', 'b', 'd', 'e', 'g'],
  '3': ['a', 'b', 'c', 'd', 'g'],
  '4': ['b', 'c', 'f', 'g'],
  '5': ['a', 'c', 'd', 'f', 'g'],
  '6': ['a', 'c', 'd', 'e', 'f', 'g'],
  '7': ['a', 'b', 'c'],
  '8': ['a', 'b', 'c', 'd', 'e', 'f', 'g'],
  '9': ['a', 'b', 'c', 'd', 'f', 'g'],
};

const ALL_SEGMENTS = ['a', 'b', 'c', 'd', 'e', 'f', 'g'];

// ============================================
// SAFE DISPLAY DIGIT COMPONENT
// ============================================

function SafeDisplayDigit({ digit }: { digit: string | null }) {
  const activeSegments = digit ? SEGMENT_MAP[digit] || [] : [];

  return (
    <div className="safe__display-digit">
      {ALL_SEGMENTS.map((segment) => (
        <div
          key={segment}
          className={cn(
            'safe__segment',
            `safe__segment--${segment}`,
            activeSegments.includes(segment) && 'safe__segment--active'
          )}
        />
      ))}
    </div>
  );
}

// ============================================
// PARTICLE EXPLOSION COMPONENT
// ============================================

function ParticleExplosion({
  isActive,
  onComplete
}: {
  isActive: boolean;
  onComplete: () => void;
}): React.ReactElement | null {
  useEffect(() => {
    if (isActive) {
      const timer = setTimeout(onComplete, 800);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [isActive, onComplete]);

  if (!isActive) return null;

  // Generate 24 particles in a circle
  const particles = Array.from({ length: 24 }, (_, i) => {
    const angle = (i / 24) * 360;
    const distance = 40 + Math.random() * 30;
    return { angle, distance, delay: Math.random() * 0.1 };
  });

  return (
    <div className="particle-explosion">
      {particles.map((p, i) => (
        <div
          key={i}
          className="particle-explosion__particle"
          style={{
            '--angle': `${p.angle}deg`,
            '--distance': `${p.distance}px`,
            '--delay': `${p.delay}s`,
          } as React.CSSProperties}
        />
      ))}
    </div>
  );
}

// ============================================
// MAIN COMPONENT
// ============================================

export function DepositSection({
  bonusPercentage = 0,
  isLoading: _isLoading = false,
  onDepositConfirm,
}: DepositSectionProps) {
  // Sound effects
  const { play: playSound } = useSound();

  // Calculator state
  const [isCalculatorOpen, setIsCalculatorOpen] = useState(false);
  const [calculatorValue, setCalculatorValue] = useState('0');

  // Display state
  const [displayAmount, setDisplayAmount] = useState<number | null>(null);
  const [displayValue, setDisplayValue] = useState<number>(0);

  // Animation state
  const [animationPhase, setAnimationPhase] = useState<
    'idle' | 'waiting' | 'bonus-flying' | 'exploding' | 'counting' | 'done'
  >('idle');

  // Warning popup state
  const [showWarning, setShowWarning] = useState(false);

  // Idle hint state
  const [showIdleHint, setShowIdleHint] = useState(false);
  const [showDialHint, setShowDialHint] = useState(false);
  const idleTimerRef = useRef<NodeJS.Timeout | null>(null);
  const dialTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Refs for animation
  const bonusRef = useRef<HTMLSpanElement>(null);
  const displayRef = useRef<HTMLDivElement>(null);

  // Reset idle timer (for keypad hint)
  const resetIdleTimer = useCallback(() => {
    setShowIdleHint(false);
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
    }
    // Only start timer if no amount entered yet and calculator is closed
    if (displayAmount === null && !isCalculatorOpen) {
      idleTimerRef.current = setTimeout(() => {
        setShowIdleHint(true);
      }, 5000);
    }
  }, [displayAmount, isCalculatorOpen]);

  // Reset dial hint timer (for dial after amount entered)
  const resetDialTimer = useCallback(() => {
    setShowDialHint(false);
    if (dialTimerRef.current) {
      clearTimeout(dialTimerRef.current);
    }
  }, []);

  // Start dial hint timer after animation completes
  useEffect(() => {
    if (animationPhase === 'done' && displayAmount !== null) {
      dialTimerRef.current = setTimeout(() => {
        setShowDialHint(true);
      }, 5000);
    }
    return () => {
      if (dialTimerRef.current) {
        clearTimeout(dialTimerRef.current);
      }
    };
  }, [animationPhase, displayAmount]);

  // Start idle timer on mount and reset on relevant state changes
  useEffect(() => {
    resetIdleTimer();
    return () => {
      if (idleTimerRef.current) {
        clearTimeout(idleTimerRef.current);
      }
    };
  }, [resetIdleTimer]);

  // Format display value to 4 digits (pad with leading spaces/nulls)
  const getDisplayDigits = (value: number | null): (string | null)[] => {
    if (value === null) return [null, null, null, null];
    const str = Math.min(9999, Math.floor(value)).toString();
    const padded = str.padStart(4, ' ');
    return padded.split('').map(c => c === ' ' ? null : c);
  };

  const displayDigits = getDisplayDigits(displayValue);

  // Handle calculator button press (click sound handled globally)
  const handleButtonPress = (type: ButtonType) => {
    if (type === 'del') {
      const newValue = calculatorValue.slice(0, -1);
      setCalculatorValue(newValue === '' ? '0' : newValue);
    } else if (type !== 'ok') {
      if (calculatorValue === '0') {
        if (type !== '0') {
          setCalculatorValue(type);
        }
      } else {
        if (calculatorValue.length < 4) {
          setCalculatorValue(prev => prev + type);
        }
      }
    }
  };

  // Handle back (DEL when value is 0) - click sound handled globally
  const handleBack = () => {
    setIsCalculatorOpen(false);
    setCalculatorValue('0');
  };

  // Handle confirm (OK pressed)
  const handleConfirm = (value: string) => {
    const amount = parseInt(value) || 0;
    setDisplayAmount(amount);
    setDisplayValue(amount);
    setIsCalculatorOpen(false);
    setCalculatorValue('0');

    // Start bonus animation if there's a bonus, otherwise go straight to done
    // NOTE: Don't call onDepositConfirm here - wait for dial click + warning confirmation
    if (bonusPercentage > 0) {
      setAnimationPhase('waiting');
    } else {
      setAnimationPhase('done');
    }
  };

  // Bonus animation sequence
  useEffect(() => {
    if (animationPhase === 'waiting') {
      const timer = setTimeout(() => {
        setAnimationPhase('bonus-flying');
      }, 2000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [animationPhase]);

  useEffect(() => {
    if (animationPhase === 'bonus-flying') {
      const timer = setTimeout(() => {
        setAnimationPhase('exploding');
        playSound('bonusExplode'); // Explosion sound
      }, 600);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [animationPhase, playSound]);

  const handleExplosionComplete = useCallback(() => {
    setAnimationPhase('counting');
  }, []);

  // Counter animation
  useEffect(() => {
    if (animationPhase === 'counting' && displayAmount !== null) {
      const targetValue = Math.floor(displayAmount * (1 + bonusPercentage / 100));
      const startValue = displayAmount;

      // Simple timing: 1.5s tick sound, then cash register
      const countingDuration = 1500; // Match the tick sound length
      const cashRegisterDelay = 200; // Small gap before cash register

      const startTime = Date.now();

      // Play tick sound once at start
      playSound('counterTick');

      const animate = () => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / countingDuration, 1);

        // Ease out cubic
        const eased = 1 - Math.pow(1 - progress, 3);
        const currentValue = Math.floor(startValue + (targetValue - startValue) * eased);

        setDisplayValue(currentValue);

        if (progress < 1) {
          requestAnimationFrame(animate);
        } else {
          // Play cash register after tick sound finishes
          // NOTE: Don't call onDepositConfirm here - wait for dial click + warning confirmation
          setTimeout(() => {
            playSound('bonusComplete');
            setAnimationPhase('done');
          }, cashRegisterDelay);
        }
      };

      requestAnimationFrame(animate);
    }
  }, [animationPhase, displayAmount, bonusPercentage, onDepositConfirm, playSound]);

  // Open calculator when keypad is clicked - click sound handled globally
  const handleKeypadClick = () => {
    resetIdleTimer();
    if (animationPhase === 'idle' || animationPhase === 'done') {
      setIsCalculatorOpen(true);
      setAnimationPhase('idle');
    }
  };

  // Click dial to proceed with deposit
  const handleDialClick = () => {
    if (animationPhase === 'done' && displayAmount !== null) {
      playSound('vaultClick'); // Vault dial sound
      resetDialTimer();
      setShowWarning(true);
    }
  };

  // Warning popup handlers - click sound handled globally
  const handleWarningProceed = () => {
    setShowWarning(false);
    // TODO: Proceed with deposit - call parent callback or navigate
    const finalAmount = displayValue;
    onDepositConfirm?.(displayAmount!, finalAmount);
  };

  const handleWarningGoBack = () => {
    setShowWarning(false);
  };

  const hasBonus = bonusPercentage > 0;
  const showBonusLabel = animationPhase === 'idle' || animationPhase === 'waiting';
  const isBonusFlying = animationPhase === 'bonus-flying';
  const isBonusHidden = animationPhase === 'exploding' || animationPhase === 'counting' || animationPhase === 'done';
  const isExploding = animationPhase === 'exploding';

  return (
    <div className={cn('deposit-section', showIdleHint && 'deposit-section--idle-hint')}>
      {/* Bonus Display */}
      {hasBonus && (
        <div className={cn(
          'deposit-section__bonus',
          animationPhase === 'waiting' && 'deposit-section__bonus--fade-label'
        )}>
          <span className={cn(
            'deposit-section__bonus-label',
            !showBonusLabel && 'deposit-section__bonus-label--hidden'
          )}>
            Bonus
          </span>
          <span className={cn(
            'deposit-section__bonus-dots',
            !showBonusLabel && 'deposit-section__bonus-dots--hidden'
          )}>
            :
          </span>
          <span
            ref={bonusRef}
            className={cn(
              'deposit-section__bonus-value',
              isBonusFlying && 'deposit-section__bonus-value--flying',
              isBonusHidden && 'deposit-section__bonus-value--hidden'
            )}
          >
            {bonusPercentage}%
          </span>
        </div>
      )}

      {/* Safe Vault */}
      <div className="safe">
        <div className="safe__door">
          {/* Hinges */}
          <div className="safe__hinge safe__hinge--top" />
          <div className="safe__hinge safe__hinge--bottom" />

          {/* 4-digit LCD display */}
          <div className="safe__display" ref={displayRef}>
            {displayDigits.map((digit, i) => (
              <SafeDisplayDigit key={i} digit={digit} />
            ))}

            {/* Particle explosion */}
            <ParticleExplosion
              isActive={isExploding}
              onComplete={handleExplosionComplete}
            />
          </div>

          {/* Dial - clickable to proceed */}
          <div
            className={cn(
              'safe__dial',
              (animationPhase === 'done' && displayAmount !== null) && 'safe__dial--active',
              showDialHint && 'safe__dial--hint'
            )}
            onClick={handleDialClick}
          >
            <div className="safe__dial-outer">
              <div className="safe__dial-inner">
                <div className="safe__dial-center" />
              </div>
              {/* Dial markers */}
              <div className="safe__dial-marker safe__dial-marker--1" />
              <div className="safe__dial-marker safe__dial-marker--2" />
              <div className="safe__dial-marker safe__dial-marker--3" />
              <div className="safe__dial-marker safe__dial-marker--4" />
            </div>
          </div>

          {/* Keypad - clickable to open calculator */}
          <div className="safe__keypad" onClick={handleKeypadClick}>
            {/* LED indicator */}
            <div className={cn(
              'safe__keypad-led',
              displayAmount !== null && 'safe__keypad-led--green'
            )} />
            {/* Display showing asterisks */}
            <div className="safe__keypad-display">
              <div className="safe__keypad-asterisk" />
              <div className="safe__keypad-asterisk" />
              <div className="safe__keypad-asterisk" />
              <div className="safe__keypad-asterisk" />
            </div>
            {/* Circular buttons */}
            <div className="safe__keypad-buttons">
              <div className="safe__key" />
              <div className="safe__key" />
              <div className="safe__key" />
              <div className="safe__key" />
              <div className="safe__key" />
              <div className="safe__key" />
              <div className="safe__key" />
              <div className="safe__key" />
              <div className="safe__key" />
            </div>
          </div>

          {/* Bolts */}
          <div className="safe__bolt safe__bolt--1" />
          <div className="safe__bolt safe__bolt--2" />
          <div className="safe__bolt safe__bolt--3" />
        </div>
      </div>

      {/* Calculator Overlay */}
      {isCalculatorOpen && (
        <div className="calculator-overlay">
          <button
            className="calculator-overlay__close"
            onClick={handleBack}
            aria-label="Close calculator"
          >
            ×
          </button>
          <Calculator
            value={calculatorValue}
            maxDigits={4}
            minAmount={10}
            onButtonPress={handleButtonPress}
            onBack={handleBack}
            onConfirm={handleConfirm}
          />
        </div>
      )}

      {/* Warning Popup */}
      {showWarning && (
        <div className="warning-popup-overlay">
          <div className="warning-popup">
            <p className="warning-popup__message">
              Hər depozitdən əvvəl balansınız sıfırlanacaq. Davam etməzdən əvvəl bütün balansınızı oynadığınızdan əmin olun.
            </p>
            <div className="warning-popup__buttons">
              <button
                className="warning-popup__button warning-popup__button--proceed"
                onClick={handleWarningProceed}
              >
                Davam et
              </button>
              <button
                className="warning-popup__button warning-popup__button--back"
                onClick={handleWarningGoBack}
              >
                Geri qayıt
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
